import { useState } from 'react'
import { BookOpen, FolderTree, FileText, ChevronRight, ChevronDown, Loader2, ArrowLeft } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useR2List, useR2Read } from '@/hooks/useApi'

const ROOT_PREFIXES = [
  { prefix: 'core/', label: 'Core', desc: 'Identity and architecture' },
  { prefix: 'self/', label: 'Self', desc: 'Fixes, capabilities, decisions' },
  { prefix: 'modules/', label: 'Modules', desc: 'Per-worker knowledge' },
  { prefix: 'platform/', label: 'Platform', desc: 'Known issues, risk, deployments' },
  { prefix: 'drift/', label: 'Drift', desc: 'Detected drift entries' },
  { prefix: 'records/', label: 'Records', desc: 'Failures, decisions, audits' },
  { prefix: 'evidence/', label: 'Evidence', desc: 'Test results, diagnostics' },
  { prefix: 'indexes/', label: 'Indexes', desc: 'Issue and knowledge indexes' },
  { prefix: 'archives/', label: 'Archives', desc: 'Superseded entries' },
  { prefix: 'playbooks/', label: 'Playbooks', desc: 'Promoted operational playbooks' },
]

function FolderBrowser({ prefix, onSelect }: { prefix: string; onSelect: (key: string) => void }) {
  const { data, isLoading } = useR2List(prefix, 200)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  if (isLoading) return <div className="py-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-text-muted" /></div>
  if (!data?.objects?.length) return <div className="py-4 text-center text-sm text-text-muted">No objects under {prefix}</div>

  const folders = new Map<string, string[]>()
  const files: string[] = []

  for (const obj of data.objects) {
    const rel = obj.key.slice(prefix.length)
    const slashIdx = rel.indexOf('/')
    if (slashIdx > 0) {
      const folder = rel.slice(0, slashIdx)
      if (!folders.has(folder)) folders.set(folder, [])
      folders.get(folder)!.push(obj.key)
    } else {
      files.push(obj.key)
    }
  }

  return (
    <div className="space-y-0.5">
      {[...folders.entries()].map(([folder, keys]) => (
        <div key={folder}>
          <button
            onClick={() => {
              const next = new Set(expanded)
              next.has(folder) ? next.delete(folder) : next.add(folder)
              setExpanded(next)
            }}
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-accent/10 text-left text-sm"
          >
            {expanded.has(folder) ? <ChevronDown className="h-3 w-3 text-text-muted" /> : <ChevronRight className="h-3 w-3 text-text-muted" />}
            <FolderTree className="h-3.5 w-3.5 text-accent" />
            <span className="text-text-primary">{folder}/</span>
            <Badge variant="muted" className="text-[10px] ml-auto">{keys.length}</Badge>
          </button>
          {expanded.has(folder) && (
            <div className="ml-6 border-l border-border pl-2">
              {keys.map(key => (
                <button
                  key={key}
                  onClick={() => onSelect(key)}
                  className="flex items-center gap-2 w-full px-2 py-1 rounded hover:bg-accent/10 text-left text-xs"
                >
                  <FileText className="h-3 w-3 text-text-muted" />
                  <span className="text-text-secondary truncate">{key.split('/').pop()}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      {files.map(key => (
        <button
          key={key}
          onClick={() => onSelect(key)}
          className="flex items-center gap-2 w-full px-2 py-1.5 rounded hover:bg-accent/10 text-left text-sm"
        >
          <FileText className="h-3.5 w-3.5 text-text-muted" />
          <span className="text-text-primary truncate">{key.split('/').pop()}</span>
        </button>
      ))}
    </div>
  )
}

function ObjectViewer({ objectKey, onBack }: { objectKey: string; onBack: () => void }) {
  const { data, isLoading } = useR2Read(objectKey)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Button variant="ghost" size="icon" onClick={onBack} className="h-6 w-6">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <FileText className="h-4 w-4" />
          <span className="truncate font-mono text-xs">{objectKey}</span>
          {data && <Badge variant="muted" className="ml-auto text-[10px]">{data.content_type}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-text-muted" /></div>
        ) : data ? (
          <pre className="text-xs text-text-secondary bg-background rounded-lg p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap font-mono">
            {typeof data.data === 'string' ? data.data : JSON.stringify(data.data, null, 2)}
          </pre>
        ) : (
          <div className="py-4 text-center text-sm text-text-muted">Failed to load</div>
        )}
      </CardContent>
    </Card>
  )
}

export function KnowledgePanel() {
  const [activePrefix, setActivePrefix] = useState<string | null>(null)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  if (selectedKey) {
    return <ObjectViewer objectKey={selectedKey} onBack={() => setSelectedKey(null)} />
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Knowledge Browser
            <Badge variant="muted" className="text-[10px]">R2 Memory</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {ROOT_PREFIXES.map(rp => (
              <button
                key={rp.prefix}
                onClick={() => setActivePrefix(activePrefix === rp.prefix ? null : rp.prefix)}
                className={`p-2 rounded-lg border text-left transition-colors ${activePrefix === rp.prefix ? 'border-accent bg-accent/10' : 'border-border hover:border-accent/50'}`}
              >
                <div className="text-xs font-medium text-text-primary">{rp.label}</div>
                <div className="text-[10px] text-text-muted">{rp.desc}</div>
              </button>
            ))}
          </div>
          {activePrefix && <FolderBrowser prefix={activePrefix} onSelect={setSelectedKey} />}
          {!activePrefix && <div className="text-center py-6 text-sm text-text-muted">Select a category above to browse</div>}
        </CardContent>
      </Card>
    </div>
  )
}
